/**
 * Gb74OrgReport - GB組織 診断レポート (gb-org-questionnaire-74)
 * docs/mockups/13_モック_GB組織_レポート_v4.html 準拠。
 *
 * 5セクション (組織の評価 / 社員の分布 / つながりの構造 / 評価を上げるために / おわりに)
 * + 補足資料 (部署別一覧)。組織ダイヤモンドは個人と同じ DiamondChart を使用。
 *
 * 現状データはダミー (orgReportDummy.ts)。実データ接続時は集計API + orgScore.ts に差し替える。
 */
import React, { useState } from 'react';
import { DiamondChart } from './DiamondChart';
import type { AxisKey } from '../utils/gb74Report';
import { DUMMY_ORG_REPORT, type OrgReportData } from '../utils/orgReportDummy';
import type { DeptConnectionStructure } from '../utils/deptConnectionStructure';
import { ConnectionStructureLevelBAccordion } from './ConnectionStructureLevelB';
import './Gb74OrgReport.scss';

interface Gb74OrgReportProps {
  data?: OrgReportData;
}

const GRADE_CLASS: Record<string, string> = { A: 'g-a', B: 'g-b', C: 'g-c', D: 'g-d' };
const gaugeFillClass = (grade: string) =>
  grade === 'A' ? 'gfa' : grade === 'B' ? 'gfb' : grade === 'C' ? 'gfc' : 'gfd';

// 散布図の四隅配置（レーダー DiamondChart と同一）: 左上=A論理 右上=D創造 右下=C共感 左下=B着実
const SCATTER_ANGLE: Record<AxisKey, number> = { A: 135, D: 45, C: 315, B: 225 };
const SCATTER_MAX = 12;

/** 各個人の4軸スコアを小さなダイヤ形(レーダー形)で描く */
const MiniDiamond: React.FC<{ axis: Record<AxisKey, number>; size?: number; color?: string }> = ({
  axis,
  size = 18,
  color = '#006ABD',
}) => {
  const c = size / 2;
  const rMax = size / 2 - 1;
  const pt = (k: AxisKey): string => {
    const rad = (SCATTER_ANGLE[k] * Math.PI) / 180;
    const r = (Math.min(axis[k] ?? 0, SCATTER_MAX) / SCATTER_MAX) * rMax;
    return `${c + r * Math.cos(rad)},${c - r * Math.sin(rad)}`;
  };
  const poly = (['A', 'D', 'C', 'B'] as AxisKey[]).map(pt).join(' ');
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block', overflow: 'visible' }}>
      <polygon points={poly} fill={color} fillOpacity={0.3} stroke={color} strokeWidth={1} strokeLinejoin="round" />
    </svg>
  );
};

/** axis 未保持(ダミー等)の点は座標から4隅の双一次重みで近似する */
const axisFromXY = (x: number, y: number): Record<AxisKey, number> => ({
  A: (1 - x) * (1 - y) * SCATTER_MAX,
  D: x * (1 - y) * SCATTER_MAX,
  C: x * y * SCATTER_MAX,
  B: (1 - x) * y * SCATTER_MAX,
});

/** 社員の分布 散布図。四隅=各軸(レーダーと同配置)、各点=個人のミニ・ダイヤ形 */
const ScatterPlot: React.FC<{ data: OrgReportData }> = ({ data }) => (
  <div className="orgr-sw">
    <div className="orgr-sh" />
    <div className="orgr-sv" />
    {/* 四隅の軸名（レーダーと同じ配置） */}
    <div className="orgr-sa" style={{ top: 8, left: 10, color: 'var(--A)' }}>A 論理</div>
    <div className="orgr-sa" style={{ top: 8, right: 10, color: 'var(--D)' }}>D 創造</div>
    <div className="orgr-sa" style={{ bottom: 8, right: 10, color: 'var(--C)' }}>C 共感</div>
    <div className="orgr-sa" style={{ bottom: 8, left: 10, color: 'var(--B)' }}>B 着実</div>
    {data.scatter.map((d, i) => (
      <div key={i} className="orgr-sd" style={{ top: `${d.y * 100}%`, left: `${d.x * 100}%` }}>
        <MiniDiamond axis={d.axis ?? axisFromXY(d.x, d.y)} />
      </div>
    ))}
  </div>
);

export const Gb74OrgReport: React.FC<Gb74OrgReportProps> = ({ data = DUMMY_ORG_REPORT }) => {
  const [supOpen, setSupOpen] = useState(false);
  const gradeColor =
    data.totalGrade === 'A'
      ? '#2D8A4E'
      : data.totalGrade === 'B'
        ? '#2B6CB0'
        : data.totalGrade === 'C'
          ? '#B8860B'
          : '#B33A3A';

  return (
    <div className="orgr">
      {/* Header */}
      <div className="orgr-hd">
        <div className="orgr-hd-l">GB組織 診断レポート</div>
        <div className="orgr-hd-c">{data.companyName}</div>
        <div className="orgr-hd-m">{data.meta}</div>
      </div>

      {/* 01 組織の評価 */}
      <section className="orgr-sec">
        <div className="orgr-sn">01</div>
        <div className="orgr-st">組織の評価</div>
        <div className="orgr-sl" style={{ borderColor: '#B8860B' }}>
          {data.evaluationLead}
        </div>

        <div className="orgr-cd">
          <div className="orgr-score-row">
            <div className="orgr-score-box">
              <div className="orgr-score-num" style={{ color: gradeColor }}>
                {data.totalScore}
                <span className="orgr-score-max">/100</span>
              </div>
              <div className="orgr-score-label">組織の総合評価</div>
              <div>
                <span className={`orgr-bg ${GRADE_CLASS[data.totalGrade]}`}>
                  {data.totalGrade} —{' '}
                  {data.totalGrade === 'C'
                    ? '要対応'
                    : data.totalGrade === 'B'
                      ? '注意'
                      : data.totalGrade === 'A'
                        ? '良好'
                        : '緊急'}
                </span>
              </div>
            </div>
            <div className="orgr-diamond-box">
              <DiamondChart
                axis={data.axis}
                maxValue={12}
                baseValue={0}
                showBreakthrough={false}
                labels={{ A: 'A 論理', B: 'B 着実', C: 'C 共感', D: 'D 創造' }}
                monoColor="#006ABD"
                size={210}
              />
              <div className="orgr-diamond-note">{data.axisNote}</div>
            </div>
          </div>

          <div className="orgr-gauges">
            {data.gauges.map((g) => (
              <div className="orgr-gr" key={g.label}>
                <div className={`orgr-gl${g.emphasis ? ' emphasis' : ''}`}>{g.label}</div>
                <div className="orgr-gbg">
                  <div
                    className={`orgr-gf ${gaugeFillClass(g.grade)}`}
                    style={{ width: `${g.score}%` }}
                  />
                </div>
                <div className={`orgr-gg ${GRADE_CLASS[g.grade]}`}>{g.grade}</div>
                <div className="orgr-gp">{g.score}点</div>
              </div>
            ))}
          </div>
          <div className="orgr-nt">
            A（80〜100）良好 / B（60〜79）注意 / C（40〜59）要対応 / D（0〜39）緊急
          </div>

          {data.herrmannLoad && (
            <div className="orgr-load">
              <div className="orgr-load-head">組織の噛み合わせ（コミュニケーション負荷）</div>
              <div className="orgr-load-cell">{data.herrmannLoad.cellLabel}</div>
              <div className="orgr-load-metrics">
                <span>
                  分極 {data.herrmannLoad.polarizationPct}%
                  <em>（{data.herrmannLoad.worstDiagonalLabel}）</em>
                </span>
                <span>橋渡し {data.herrmannLoad.bridgePct}%</span>
                <span>緩衝 {data.herrmannLoad.bufferPct}%</span>
                <span>脆さ {data.herrmannLoad.fragilityPct}%</span>
              </div>
              <div className="orgr-load-adj">
                {data.herrmannLoad.applied && data.herrmannLoad.nudgePct > 0 ? (
                  <>
                    つながりスコア <b>{data.herrmannLoad.connBefore}</b> →{' '}
                    <b>{data.herrmannLoad.connAfter}</b>（負荷補正 −{data.herrmannLoad.nudgePct}%）
                  </>
                ) : (
                  <>つながりスコア補正なし（据え置き）</>
                )}
              </div>
              <div className="orgr-load-pi">
                <b>次の一手：</b>
                {data.herrmannLoad.piHeadline}
                <em>（テーマ：{data.herrmannLoad.piTheme}）</em>
              </div>
              {data.herrmannLoad.note && (
                <div className="orgr-load-note">{data.herrmannLoad.note}</div>
              )}
              {/* 注記(ハーマン/話しにくさ・学術根拠レベル3…)は要望により非表示 */}
            </div>
          )}
        </div>
      </section>

      {/* 02 社員の分布 */}
      <section className="orgr-sec">
        <div className="orgr-sn">02</div>
        <div className="orgr-st">社員の分布</div>
        <div className="orgr-sl" style={{ borderColor: '#8B5CF6' }}>
          {data.distributionLead}
        </div>
        <div className="orgr-cd orgr-cd-tight">
          <ScatterPlot data={data} />
        </div>
        <div className="orgr-nt">
          ※ 位置に優劣はありません。各点は個人の思考タイプ（ダイヤ形）を表し、回答傾向をもとに持ち味の方向を概観したものです。
        </div>
      </section>

      {/* 03 つながりの構造 */}
      <section className="orgr-sec">
        <div className="orgr-sn">03</div>
        <div className="orgr-st">つながりの構造</div>
        <div className="orgr-sl" style={{ borderColor: '#B33A3A' }}>
          {data.connectionLead}
        </div>
        <div className="orgr-cd orgr-cd-tight">
          {data.consultNetwork ? (
            /* レベルB: 部門ピル一覧＋クリック展開のネットワーク図 (image2/3)。実名表示ON */
            <ConnectionStructureLevelBAccordion network={data.consultNetwork} showRealNames={true} />
          ) : (
            <>
              {data.connectionStructure ? (
                <DeptStructureView structure={data.connectionStructure} />
              ) : (
                <NetworkDiagram />
              )}
              <div className="orgr-net-legend">
                <span style={{ color: '#2D8A4E' }}>△ カバーし合える</span>
                <span style={{ color: '#3B82F6' }}>○ つながりあり</span>
                <span style={{ color: '#B8860B' }}>◇ 橋渡し</span>
                <span style={{ color: '#B33A3A' }}>∨ 1人に集中(V字の底)</span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* 04 評価を上げるために */}
      <section className="orgr-sec">
        <div className="orgr-sn">04</div>
        <div className="orgr-st">評価を上げるために</div>
        <div className="orgr-sl" style={{ borderColor: '#2D8A4E' }}>
          {data.improvementLead}
        </div>
        <div className="orgr-ic">
          <div className="orgr-target-row">
            <div className="orgr-target-item">
              <div className="orgr-target-cap">現在</div>
              <div className="orgr-target-cur">{data.totalScore}</div>
            </div>
            <div className="orgr-target-arrow">→</div>
            <div className="orgr-target-item">
              <div className="orgr-target-cap">目標</div>
              <div className="orgr-target-goal">{data.improvementTarget}</div>
            </div>
            <div className="orgr-target-bar">
              <div className="orgr-gbg" style={{ height: 10, position: 'relative' }}>
                <div className="orgr-gf gfc" style={{ width: `${data.totalScore}%` }} />
                <div className="orgr-target-mark" style={{ left: `${data.improvementTarget}%` }} />
              </div>
            </div>
          </div>

          {data.improvements.map((imp) => (
            <div className="orgr-is" key={imp.rank}>
              <div className="orgr-isn" style={{ background: imp.rankColor }}>
                {imp.rank}
              </div>
              <div className="orgr-isb">
                <div className="orgr-ist">{imp.title}</div>
                <div className="orgr-isr">{imp.reason}</div>
                <div className="orgr-isa">
                  <div className="orgr-isa-l">今月の取り組み</div>
                  {imp.action}
                </div>
                <div className="orgr-ism">{imp.effect}</div>
              </div>
            </div>
          ))}
          <div className="orgr-nt" style={{ marginTop: 12 }}>
            ※ 改善見込みは類似企業の傾向に基づく推計です。実際の変化は状況により異なります。
          </div>
        </div>
      </section>

      {/* 補足資料 */}
      <div className="orgr-sup">
        <div className="orgr-sup-title">補足資料</div>
        <div className="orgr-sup-toggle" onClick={() => setSupOpen((v) => !v)} role="button">
          部署別の一覧
          <span className="orgr-sup-arrow">{supOpen ? '▲' : '▼'}</span>
        </div>
        {supOpen && (!data.heatRows || data.heatRows.length === 0) && (
          <div className="orgr-sup-content">
            <div className="orgr-nt">
              各部署で7名以上の回答が集まると、部署別の一覧を表示します（個人が特定されないよう、少人数の部署は非表示にしています）。
            </div>
          </div>
        )}
        {supOpen && data.heatRows && data.heatRows.length > 0 && (
          <div className="orgr-sup-content">
            <table className="orgr-ht">
              <thead>
                <tr>
                  <th />
                  {data.heatHeader.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.heatRows.map((row) => (
                  <tr key={row.dept}>
                    <td>
                      <div className="orgr-hd2">{row.dept}</div>
                    </td>
                    {row.cells.map((c, i) => (
                      <td key={i}>
                        <div className={`orgr-hc h${c.grade.toLowerCase()}`}>{c.grade}</div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="orgr-nt">A=良好 B=注意 C=要対応 D=緊急</div>
          </div>
        )}
      </div>

      <div className="orgr-footer">GB組織 診断レポート｜GBなび</div>
    </div>
  );
};

/** 部署レベルのつながり構造ビュー (deptConnectionStructure のレベルA算出結果を表示) */
const ROLE_META: Record<string, { mark: string; color: string; label: string }> = {
  triangle: { mark: '△', color: '#2D8A4E', label: 'カバーし合える' },
  bridge: { mark: '◇', color: '#B8860B', label: '橋渡し' },
  bottleneck: { mark: '∨', color: '#B33A3A', label: '1人に集中(V字の底)' },
  spoke: { mark: '○', color: '#3B82F6', label: 'つながりあり' },
  isolated: { mark: '–', color: '#888', label: '孤立しがち' },
};

const DeptStructureView: React.FC<{ structure: DeptConnectionStructure }> = ({ structure }) => (
  <div className="orgr-dcs">
    {structure.nodes.map((node) => {
      const m = ROLE_META[node.role] ?? ROLE_META.triangle;
      return (
        <div className="orgr-dcs-row" key={node.dept}>
          <div className="orgr-dcs-mark" style={{ color: m.color }}>
            {m.mark}
          </div>
          <div className="orgr-dcs-name">{node.dept}</div>
          <div className="orgr-dcs-role" style={{ color: m.color }}>
            {m.label}
          </div>
          <div className="orgr-dcs-bars">
            <div className="orgr-dcs-bar-row">
              <span className="orgr-dcs-bar-cap">つながり</span>
              <span className="orgr-dcs-bar-bg">
                <span
                  className="orgr-dcs-bar-fill"
                  style={{ width: `${Math.round(node.degree * 100)}%`, background: '#3B82F6' }}
                />
              </span>
            </div>
            <div className="orgr-dcs-bar-row">
              <span className="orgr-dcs-bar-cap">仲介度</span>
              <span className="orgr-dcs-bar-bg">
                <span
                  className="orgr-dcs-bar-fill"
                  style={{ width: `${Math.round(node.betweenness * 100)}%`, background: m.color }}
                />
              </span>
            </div>
          </div>
        </div>
      );
    })}
    {structure.triangles.length > 0 && (
      <div className="orgr-dcs-note">
        △ 三角形(相互にカバーし合える3部署):{' '}
        {structure.triangles.map((t) => t.join('・')).join(' / ')}
      </div>
    )}
  </div>
);

/** つながりネットワーク図 (モック13のSVGを移植。ダミー配置) */
const NetworkDiagram: React.FC = () => (
  <svg
    width="100%"
    viewBox="0 0 500 360"
    style={{ display: 'block', margin: '0 auto', maxWidth: 480 }}
  >
    {/* 製造部 */}
    <ellipse
      cx="150"
      cy="180"
      rx="115"
      ry="90"
      fill="#3B82F6"
      opacity="0.04"
      stroke="#3B82F6"
      strokeWidth="0.5"
      strokeDasharray="4"
    />
    <text x="150" y="96" textAnchor="middle" fontSize="11" fill="#3B82F6" fontWeight="600">
      製造部
    </text>
    <line x1="95" y1="160" x2="145" y2="200" stroke="#B33A3A" strokeWidth="2" opacity="0.5" />
    <line x1="195" y1="160" x2="145" y2="200" stroke="#B33A3A" strokeWidth="2" opacity="0.5" />
    <line x1="125" y1="240" x2="145" y2="200" stroke="#B33A3A" strokeWidth="1.5" opacity="0.4" />
    <line x1="165" y1="240" x2="145" y2="200" stroke="#B33A3A" strokeWidth="1.5" opacity="0.4" />
    <line x1="75" y1="220" x2="145" y2="200" stroke="#B33A3A" strokeWidth="1" opacity="0.3" />
    <text x="72" y="152" fontSize="9" fill="#B33A3A" fontWeight="500">
      ∨ 集中
    </text>
    <polygon
      points="215,160 245,220 185,220"
      fill="#2D8A4E"
      opacity="0.1"
      stroke="#2D8A4E"
      strokeWidth="1.5"
    />
    <text x="225" y="152" fontSize="9" fill="#2D8A4E" fontWeight="500">
      △
    </text>
    <circle cx="145" cy="200" r="13" fill="#3B82F6" stroke="#fff" strokeWidth="2" />
    <text x="145" y="204" textAnchor="middle" fontSize="7.5" fill="#fff" fontWeight="600">
      田中
    </text>
    {[
      [95, 160],
      [195, 160],
      [125, 240],
      [165, 240],
      [75, 220],
      [215, 160],
      [245, 220],
      [185, 220],
    ].map(([x, y], i) => (
      <circle
        key={i}
        cx={x}
        cy={y}
        r="9"
        fill="#3B82F6"
        opacity="0.7"
        stroke="#fff"
        strokeWidth="1.5"
      />
    ))}
    {/* 営業部 */}
    <ellipse
      cx="380"
      cy="145"
      rx="85"
      ry="65"
      fill="#EC4899"
      opacity="0.04"
      stroke="#EC4899"
      strokeWidth="0.5"
      strokeDasharray="4"
    />
    <text x="380" y="86" textAnchor="middle" fontSize="11" fill="#EC4899" fontWeight="600">
      営業部
    </text>
    <polygon
      points="350,130 385,130 368,158"
      fill="#2D8A4E"
      opacity="0.1"
      stroke="#2D8A4E"
      strokeWidth="1.5"
    />
    <polygon
      points="385,130 415,158 368,158"
      fill="#2D8A4E"
      opacity="0.08"
      stroke="#2D8A4E"
      strokeWidth="1"
    />
    <text x="398" y="120" fontSize="9" fill="#2D8A4E" fontWeight="500">
      △△
    </text>
    {[
      [350, 130],
      [385, 130],
      [368, 158],
      [415, 158],
      [345, 175],
    ].map(([x, y], i) => (
      <circle
        key={i}
        cx={x}
        cy={y}
        r="9"
        fill="#EC4899"
        opacity="0.7"
        stroke="#fff"
        strokeWidth="1.5"
      />
    ))}
    {/* 管理部 */}
    <ellipse
      cx="375"
      cy="295"
      rx="75"
      ry="48"
      fill="#8B5CF6"
      opacity="0.04"
      stroke="#8B5CF6"
      strokeWidth="0.5"
      strokeDasharray="4"
    />
    <text x="375" y="256" textAnchor="middle" fontSize="11" fill="#8B5CF6" fontWeight="600">
      管理部
    </text>
    <line x1="355" y1="286" x2="375" y2="314" stroke="#B8860B" strokeWidth="1.5" opacity="0.5" />
    <line x1="395" y1="286" x2="375" y2="314" stroke="#B8860B" strokeWidth="1.5" opacity="0.5" />
    <line x1="410" y1="306" x2="375" y2="314" stroke="#B8860B" strokeWidth="1" opacity="0.4" />
    <text x="420" y="322" fontSize="9" fill="#B8860B" fontWeight="500">
      ∨ 社長
    </text>
    <circle cx="375" cy="314" r="11" fill="#8B5CF6" stroke="#fff" strokeWidth="2" />
    <text x="375" y="318" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="600">
      社長
    </text>
    {[
      [355, 286],
      [395, 286],
      [410, 306],
      [345, 310],
    ].map(([x, y], i) => (
      <circle
        key={i}
        cx={x}
        cy={y}
        r="9"
        fill="#8B5CF6"
        opacity="0.7"
        stroke="#fff"
        strokeWidth="1.5"
      />
    ))}
    {/* 部署間(弱) */}
    <line x1="245" y1="190" x2="345" y2="175" stroke="#999" strokeWidth="0.5" strokeDasharray="3" />
    <text x="290" y="178" textAnchor="middle" fontSize="8" fill="#999">
      部署間（弱）
    </text>
  </svg>
);

export default Gb74OrgReport;
